import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { useI18n } from "../context/I18nContext";
import { api, resolveImage, mailto } from "../lib/api";
import ClassicCard from "../components/ClassicCard";
import Seo from "../components/Seo";

export default function Home() {
  const { lang, t } = useI18n();
  const [settings, setSettings] = useState(null);
  const [featured, setFeatured] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, f, r, p] = await Promise.all([
          api.get("/settings"),
          api.get("/classics", { params: { featured: true } }),
          api.get("/rooms"),
          api.get("/projects"),
        ]);
        setSettings(s.data);
        setFeatured((f.data.items || []).slice(0, 4));
        setRooms((r.data.items || []).slice(0, 3));
        setProjects((p.data.items || []).slice(0, 3));
      } catch (e) {
        console.error("home load error", e);
      }
    })();
  }, []);

  const manifesto = settings
    ? lang === "nl"
      ? settings.manifesto_nl
      : settings.manifesto_en
    : "";

  const heroImg = resolveImage(settings?.hero_image_url);

  return (
    <div data-testid="page-home">
      <Seo
        path="/"
        titleNl="Tijdloze vorm. Perfecte ruimte."
        titleEn="Timeless form. Perfect space."
        descNl="DeStoel.com is een Nederlandse studio voor interieuradvies en designcuratie, sinds 2001 — iconische 20e-eeuwse meubels en verlichting voor architecten, designstudio's, particulieren en advocatenkantoren."
        descEn="DeStoel.com is a Dutch studio for interior advice and design curation, since 2001 — iconic 20th-century furniture and lighting for architects, design studios, private clients and law firms."
      />
      {/* HERO */}
      <section className="relative">
        <div className="relative h-[88vh] min-h-[600px] w-full overflow-hidden">
          <img
            src={heroImg}
            alt="DeStoel.com"
            className="absolute inset-0 w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.src = resolveImage(null);
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-stoel-paper/0 via-stoel-paper/0 to-stoel-paper" />
          <div className="absolute inset-0 bg-gradient-to-r from-stoel-paper/40 via-stoel-paper/0 to-transparent" />

          <div className="relative stoel-container h-full flex flex-col justify-end pb-16 md:pb-24">
            <div className="stoel-eyebrow mb-6 animate-fade-up">
              {t.home.eyebrow}
            </div>
            <h1
              data-testid="hero-title"
              className="stoel-display text-5xl sm:text-6xl md:text-7xl lg:text-[88px] max-w-4xl animate-fade-up"
              style={{ animationDelay: "120ms" }}
            >
              {t.tagline}
            </h1>
            <div
              className="mt-10 flex flex-wrap items-center gap-4 animate-fade-up"
              style={{ animationDelay: "260ms" }}
            >
              <a
                href={mailto(lang === "nl" ? "Projectaanvraag DeStoel.com" : "Project enquiry DeStoel.com")}
                className="stoel-btn"
                data-testid="hero-cta-primary"
              >
                {t.home.heroCta} <ArrowUpRight size={16} />
              </a>
              <Link to="/klassiekers" className="stoel-btn-ghost" data-testid="hero-cta-secondary">
                {t.home.heroSecondary} <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="stoel-container py-20 md:py-32">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="stoel-eyebrow">— Manifest</div>
          </div>
          <div className="md:col-span-8">
            <p
              data-testid="manifesto-text"
              className="text-2xl md:text-3xl lg:text-[34px] font-light leading-[1.32] tracking-tight text-stoel-ink whitespace-pre-line"
            >
              {manifesto}
            </p>
          </div>
        </div>
      </section>

      <div className="stoel-divider" />

      {/* APPROACH */}
      <section className="stoel-container py-20 md:py-28">
        <div className="grid md:grid-cols-12 gap-10 mb-14">
          <div className="md:col-span-4">
            <div className="stoel-eyebrow mb-3">— 01 / 03</div>
            <h2 className="stoel-display text-4xl md:text-5xl">
              {t.home.approachTitle}
            </h2>
          </div>
          <div className="md:col-span-7 md:col-start-6 flex items-end">
            <p className="text-base md:text-lg text-stoel-inkSoft leading-relaxed max-w-xl">
              {t.home.approachIntro}
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-10 md:gap-14 mt-10">
          {t.home.steps.map((s, i) => (
            <div
              key={s.t}
              data-testid={`approach-step-${i}`}
              className="border-t border-stoel-ink pt-6"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="text-stoel-terracotta tabular-nums text-sm tracking-wider">
                  0{i + 1}
                </span>
                <span className="h-px flex-1 bg-stoel-line" />
              </div>
              <h3 className="text-2xl font-light text-stoel-ink mb-3">{s.t}</h3>
              <p className="text-sm text-stoel-inkSoft leading-relaxed">
                {s.b}
              </p>
            </div>
          ))}
        </div>
      </section>

      <div className="stoel-divider" />

      {/* FEATURED CLASSICS */}
      <section className="stoel-container py-20 md:py-28">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-4">
          <div>
            <div className="stoel-eyebrow mb-3">— 02 / 03</div>
            <h2 className="stoel-display text-4xl md:text-5xl">
              {t.home.featuredTitle}
            </h2>
            <p className="mt-3 text-stoel-inkSoft max-w-md">
              {t.home.featuredSubtitle}
            </p>
          </div>
          <Link
            to="/klassiekers"
            data-testid="home-view-all-classics"
            className="stoel-link text-sm text-stoel-ink uppercase tracking-[0.2em] flex items-center gap-2"
          >
            {t.common.view} <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-10">
          {featured.map((c, i) => (
            <ClassicCard key={c.id} item={c} index={i} />
          ))}
        </div>
      </section>

      <div className="stoel-divider" />

      {/* RECENT PROJECTS */}
      {projects.length > 0 && (
        <section className="stoel-container py-20 md:py-28" data-testid="home-projects-section">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-4">
            <div>
              <div className="stoel-eyebrow mb-3">— Recent</div>
              <h2 className="stoel-display text-4xl md:text-5xl">
                {t.home.projectsTitle}
              </h2>
              <p className="mt-3 text-stoel-inkSoft max-w-md">
                {t.home.projectsSubtitle}
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-10">
            {projects.map((p, i) => (
              <article
                key={p.slug}
                data-testid={`project-card-${p.slug}`}
                className="bg-stoel-paper"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-stoel-paperDeep">
                  <img
                    src={resolveImage(p.image_url)}
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.src = resolveImage(null); }}
                  />
                </div>
                <div className="pt-5">
                  <span
                    data-testid={`project-room-chip-${p.slug}`}
                    className="inline-block text-[10px] uppercase tracking-[0.25em] text-stoel-paper bg-stoel-walnut px-2 py-1 mb-4"
                  >
                    {lang === "nl" ? p.room_type_nl : p.room_type_en}
                  </span>
                  <blockquote
                    data-testid={`project-testimonial-${p.slug}`}
                    className="text-base md:text-lg text-stoel-ink leading-relaxed font-light line-clamp-3"
                  >
                    "{lang === "nl" ? p.testimonial_nl : p.testimonial_en}"
                  </blockquote>
                  <div
                    data-testid={`project-label-${p.slug}`}
                    className="mt-4 text-xs uppercase tracking-[0.22em] text-stoel-walnut"
                  >
                    {lang === "nl" ? p.client_label_nl : p.client_label_en}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      <div className="stoel-divider" />

      {/* ROOMS */}
      <section className="stoel-container py-20 md:py-28">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-4">
          <div>
            <div className="stoel-eyebrow mb-3">— 03 / 03</div>
            <h2 className="stoel-display text-4xl md:text-5xl">
              {t.home.roomsTitle}
            </h2>
            <p className="mt-3 text-stoel-inkSoft max-w-md">
              {t.home.roomsSubtitle}
            </p>
          </div>
          <Link
            to="/ruimtes"
            data-testid="home-view-all-rooms"
            className="stoel-link text-sm text-stoel-ink uppercase tracking-[0.2em] flex items-center gap-2"
          >
            {t.common.view} <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {rooms.map((r, i) => (
            <Link
              key={r.id}
              to={`/ruimtes/${r.slug}`}
              data-testid={`room-card-${r.slug}`}
              className="group block"
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <div className="relative aspect-[4/5] overflow-hidden bg-stoel-paperDeep">
                <img
                  src={resolveImage(r.hero_image_url)}
                  alt={lang === "nl" ? r.title_nl : r.title_en}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                  onError={(e) => {
                    e.currentTarget.src = resolveImage(null);
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-stoel-ink/55 to-transparent" />
                <div className="absolute bottom-5 left-5 right-5 text-stoel-paper">
                  <h3 className="text-2xl md:text-3xl font-light tracking-tight">
                    {lang === "nl" ? r.title_nl : r.title_en}
                  </h3>
                  <div className="mt-2 flex items-center gap-2 text-xs uppercase tracking-[0.22em] opacity-90">
                    {t.common.view} <ArrowRight size={14} />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-stoel-ink text-stoel-paper">
        <div className="stoel-container py-20 md:py-28 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="text-xs uppercase tracking-[0.25em] text-stoel-paper/60 mb-5">
              — Contact
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight">
              {t.home.ctaSection}
            </h2>
            <p className="mt-4 text-stoel-paper/70 max-w-md">
              {t.home.ctaSection2}
            </p>
          </div>
          <div className="md:col-span-5 md:flex md:justify-end">
            <a
              href={mailto(lang === "nl" ? "Projectaanvraag DeStoel.com" : "Project enquiry DeStoel.com")}
              data-testid="footer-cta-primary"
              className="inline-flex items-center gap-3 px-7 py-4 bg-stoel-terracotta text-stoel-paper hover:bg-stoel-terracottaDeep transition-colors text-sm tracking-wide"
            >
              {t.common.cta} <ArrowUpRight size={16} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
